import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import './Reviews.scss';
import { LuBadgeCheck } from 'react-icons/lu';
import star from '../../Components/images/star.svg';
import leftArrowWhite from '../../Components/images/assets/left-arrow-white.svg';
import rightArrowWhite from '../../Components/images/assets/right-arrow-white.svg';
import { InlineStatusLoader } from '../../Components/InlineStatusLoader/InlineStatusLoader';
import axios from "axios";

import { API_BASE_URL } from '../../config';
import { PRODUCT_REVIEW_SUBMITTED } from '../../utils/productReviewEvents';
import { reviewImageUrls } from '../../utils/mediaUrl';
const BASE_URL = API_BASE_URL;

/** 1–5 for review chip background (aligned with rating breakdown progress colors) */
function ratingChipTier(rating) {
  const n = Number(rating);
  if (!Number.isFinite(n)) return "5";
  const x = Math.round(n);
  return String(Math.min(5, Math.max(1, x)));
}

const RATING_LABELS = { 5: "Excellent", 4: "Very Good", 3: "Good", 2: "Not Bad", 1: "Bad" };

export function Reviews({ product_id }) {
  const navigate = useNavigate();
  const [ratingMeta, setRatingMeta] = useState(null);
  const [modalImages, setModalImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImagesList, setModalImagesList] = useState([]);
  const [modalCurrentIndex, setModalCurrentIndex] = useState(0);
  const [ratingsRefreshKey, setRatingsRefreshKey] = useState(0);

  useEffect(() => {
    const onReviewSubmitted = (e) => {
      if (String(e.detail?.productId) === String(product_id)) {
        setRatingsRefreshKey((k) => k + 1);
      }
    };
    window.addEventListener(PRODUCT_REVIEW_SUBMITTED, onReviewSubmitted);
    return () => window.removeEventListener(PRODUCT_REVIEW_SUBMITTED, onReviewSubmitted);
  }, [product_id]);

  useEffect(() => {
    if (!product_id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem("token");
    const config = { headers: token ? { Authorization: `Bearer ${token}` } : {} };

    const fetchRatings = axios.post(
      `${BASE_URL}/products/ratings-meta/${product_id}`,
      { page: 1, size: 10 },
      config
    );
    const fetchModals = axios.post(
      `${BASE_URL}/products/modal-images/${product_id}`,
      { page: 1, size: 20 },
      config
    );

    Promise.allSettled([fetchRatings, fetchModals])
      .then(([ratingsRes, modalsRes]) => {
        if (cancelled) return;
        if (ratingsRes.status === 'fulfilled') {
          const meta = ratingsRes.value.data;
          setRatingMeta(meta && !Array.isArray(meta) ? meta : null);
        } else {
          setRatingMeta(null);
          console.error("Reviews ratings-meta error:", ratingsRes.reason);
        }
        if (modalsRes.status === 'fulfilled') {
          const modals = Array.isArray(modalsRes.value.data) ? modalsRes.value.data : [];
          setModalImages(modals);
        } else {
          setModalImages([]);
          console.error("Reviews modal-images error:", modalsRes.reason);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [product_id, ratingsRefreshKey]);

  const allPhotosFlat = useMemo(() => {
    const fromModal = (modalImages || []).flatMap((r) => reviewImageUrls(r?.images));
    if (fromModal.length) return fromModal;
    return (ratingMeta?.reviews_data || []).flatMap((r) => reviewImageUrls(r?.images));
  }, [modalImages, ratingMeta]);
  const totalPhotos = allPhotosFlat.length;
  const total = ratingMeta?.total_ratings_count ?? 0;

  const openImageModal = (imagesList, index) => {
    setModalImagesList(imagesList);
    setModalCurrentIndex(index);
    setImageModalOpen(true);
  };
  const closeImageModal = () => setImageModalOpen(false);
  const modalPrev = () => setModalCurrentIndex((i) => Math.max(0, i - 1));
  const modalNext = () => setModalCurrentIndex((i) => Math.min(modalImagesList.length - 1, i + 1));
  const modalCurrentUrl = modalImagesList[modalCurrentIndex] || null;
  const canModalPrev = modalCurrentIndex > 0;
  const canModalNext = modalCurrentIndex < modalImagesList.length - 1;
  const specific = ratingMeta?.specific_rating_count ?? {};
  const getCount = (starLevel) => {
    if (Array.isArray(specific)) return specific[starLevel - 1] ?? 0;
    return specific[String(starLevel)] ?? specific[starLevel] ?? 0;
  };
  const count5 = getCount(5);
  const count4 = getCount(4);
  const count3 = getCount(3);
  const count2 = getCount(2);
  const count1 = getCount(1);
  const reviews = ratingMeta?.reviews_data ?? [];

  if (loading) {
    return (
      <div className="mob-reviews-ratings-container">
        <div className="detailedRatingContainer">
          <div className="headingContainer">Ratings</div>
          <div className="index-flexRow index-margin22">
            <InlineStatusLoader
              message="Loading reviews..."
              helperText="Fetching ratings and customer photos"
            />
          </div>
        </div>
      </div>
    );
  }

  const progressPct = (count, tot) => (tot > 0 ? `${(count / tot) * 100}%` : "0%");
  const hasNoReviews = total === 0 && reviews.length === 0 && allPhotosFlat.length === 0;

  if (hasNoReviews) {
    return (
      <div className="mob-reviews-ratings-container">
        <div className="mob-reviews-unavailable" role="status">
          <div className="mob-reviews-unavailable__heading">Ratings & Reviews</div>
          <p className="mob-reviews-unavailable__title">Reviews are not available</p>
          <p className="mob-reviews-unavailable__text">
            This product doesn’t have any customer reviews yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mob-reviews-ratings-container">
      <div className="detailedRatingContainer design-4">
        <div className="headingContainer">Ratings</div>
        <div className="index-flexRow index-margin22 review-container">
          <div className="index-flexColumn">
            <div className="index-flexRow index-averageRating">
              <span>{ratingMeta?.ratings_average ?? "–"}</span>
              <span className="big-star"><img src={star} alt="" /></span>
            </div>
            <div className="index-countDesc">
              {total > 0 ? `${total} Verified Buyers` : "No ratings yet"}
            </div>
          </div>
          <div className="progress-container progress-variant-1">
            {[5, 4, 3, 2, 1].map((level) => {
              const count = level === 5 ? count5 : level === 4 ? count4 : level === 3 ? count3 : level === 2 ? count2 : count1;
              return (
                <div key={level} className="index-flexRow index-ratingBarContainer">
                  <span className="row-star" data-rating={String(level)}>
                    <img src={star} alt="" />
                  </span>
                  <div className="index-rating">
                    <span className="index-ratingLevel">{RATING_LABELS[level]}</span>
                  </div>
                  <div className="progress-wrapper" style={{ "--progress-percent": progressPct(count, total) }}>
                    <progress
                      min="0"
                      max={total || 1}
                      value={count}
                      data-rating={String(level)}
                    />
                  </div>
                  <div className="index-count">{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="thumbnails-container">
        <div className="image-thumb-wrapper">
          <div className="headline">
            Customer Photos ({totalPhotos})
          </div>
          <div className="images-container">
            {allPhotosFlat.length > 0 ? (
              allPhotosFlat.slice(0, 4).map((url, i) => (
                <div
                  key={i}
                  className={`image-container${i === 3 && totalPhotos > 4 ? ' image-container--has-more' : ''}`}
                  onClick={() => openImageModal(allPhotosFlat, i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && openImageModal(allPhotosFlat, i)}
                >
                  <div className="image">
                    <img src={url} alt={`Customer ${i + 1}`} />
                    {i === 3 && totalPhotos > 4 && (
                      <span className="more-images-count">+{totalPhotos - 4} more</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="mob-reviews-empty-state">No customer photos yet</p>
            )}
          </div>
        </div>

        {imageModalOpen && modalCurrentUrl && (
          <div className="mob-reviews-image-modal" onClick={closeImageModal} role="dialog" aria-modal="true" aria-label="Customer photos">
            <div className="mob-reviews-image-modal__inner" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="mob-reviews-image-modal__close"
                onClick={closeImageModal}
                aria-label="Close"
              >
                <svg className="mob-reviews-image-modal__close-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M6.4 6.4l11.2 11.2M17.6 6.4L6.4 17.6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                </svg>
              </button>

              <div className="mob-reviews-image-modal__stage">
                <div className="mob-reviews-image-modal__main">
                  <img src={modalCurrentUrl} alt="" />
                </div>
                {modalImagesList.length > 1 && (
                  <div className="mob-reviews-image-modal__nav">
                    <button
                      type="button"
                      className="mob-reviews-image-modal__prev"
                      onClick={(e) => { e.stopPropagation(); if (canModalPrev) modalPrev(); }}
                      disabled={!canModalPrev}
                      aria-label="Previous image"
                    >
                      <img src={leftArrowWhite} alt="" />
                    </button>
                    <button
                      type="button"
                      className="mob-reviews-image-modal__next"
                      onClick={(e) => { e.stopPropagation(); if (canModalNext) modalNext(); }}
                      disabled={!canModalNext}
                      aria-label="Next image"
                    >
                      <img src={rightArrowWhite} alt="" />
                    </button>
                  </div>
                )}
              </div>

              <div className="mob-reviews-image-modal__footer">
                <div className="mob-reviews-image-modal__counter">
                  {modalCurrentIndex + 1} / {modalImagesList.length}
                </div>
                {modalImagesList.length > 1 && (() => {
                  const total = modalImagesList.length;
                  const maxThumbs = 6;
                  const start = total <= maxThumbs
                    ? 0
                    : Math.max(0, Math.min(modalCurrentIndex - 2, total - maxThumbs));
                  const end = Math.min(start + maxThumbs, total);
                  const thumbIndices = Array.from({ length: end - start }, (_, i) => start + i);
                  return (
                    <div className="mob-reviews-image-modal__thumbs">
                      {thumbIndices.map((i) => (
                        <button
                          key={i}
                          type="button"
                          className={`mob-reviews-image-modal__thumb ${i === modalCurrentIndex ? 'is-active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setModalCurrentIndex(i); }}
                          aria-label={`Image ${i + 1}`}
                          aria-current={i === modalCurrentIndex}
                        >
                          <img src={modalImagesList[i]} alt="" />
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        <div className="review-headline">
          Customer Reviews ({reviews.length})
        </div>

        <div className="user-review-container">
          {reviews.length > 0 ? (
            reviews.slice(0, 2).map((review, index) => {
              const reviewPhotos = reviewImageUrls(review.images);
              return (
              <div key={review._id || index} className="user-review-wrapper">
                <div className="main">
                  <div
                    className="review-rating-chip"
                    data-rating={ratingChipTier(review.rating)}
                    role="img"
                    aria-label={`${review.rating ?? "–"} out of 5 stars`}
                  ><span className="review-rating-chip__count">{review.rating ?? "–"}</span><span className="review-rating-chip__star" aria-hidden="true">★</span></div>
                  <div className="review-body">
                    <div className="reviewTextRwapper">
                      {review.comment || "–"}
                    </div>
                    {reviewPhotos.length > 0 && (
                      <div className="images-container">
                        <div className="thumb-images">
                          {reviewPhotos.slice(0, 4).map((image, imageIndex) => (
                            <div
                              key={imageIndex}
                              className={`images${imageIndex === 3 && reviewPhotos.length > 4 ? ' images--has-more' : ''}`}
                              onClick={() => openImageModal(reviewPhotos, imageIndex)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => e.key === "Enter" && openImageModal(reviewPhotos, imageIndex)}
                            >
                              <img
                                src={image}
                                alt={`Review ${index + 1}`}
                              />
                              {imageIndex === 3 && reviewPhotos.length > 4 && (
                                <span className="more-images-count">+{reviewPhotos.length - 4} more</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="footer">
                  <div className="footer__reviewer">
                    <LuBadgeCheck className="footer__icon" aria-hidden />
                    <span className="footer__text">
                      {review.user_type && review.user_type.toLowerCase() !== "normal"
                        ? `Verified · ${review.user_type}`
                        : "Verified customer"}
                    </span>
                  </div>
                </div>
              </div>
              );
            })
          ) : (
            <p className="mob-reviews-empty-state">No reviews yet</p>
          )}
        </div>
      </div>
      {reviews.length > 2 && (
        <div
          className="review-links"
          onClick={() => navigate(`/full-review/${product_id}`)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && navigate(`/full-review/${product_id}`)}
        >
          View all {reviews.length} reviews
        </div>
      )}
    </div>
  );
}
